import {
  brandOptions,
  organizations,
  suppliers,
  adminUnits,
  contactPersons,
} from './itemsFixtureData';

/**
 * Real, persisted Items rows for the DL-63887 epic ("D2 Playwright automation — dl-60144 Clear
 * option in Items inline-edit dropdowns", Testmo folder dl-60144, 15 cases).
 *
 * These are REAL rows in `itemsData` (not `apiTransform` GET overrides) for the same reason
 * `itemsClearFieldFixtures.ts` (DL-62706) and `clearFieldGatingProbeFixtureData.ts` (dl-62704)
 * are: the epic's own ground rule requires "the `PUT /api/items/{id}` handler must mutate the
 * store so 'saves empty and persists' is genuinely verifiable" — an `apiTransform`-frozen GET
 * response can prove the UI *rendered* empty, but can never prove a genuine server-side commit.
 * `itemsData` has no `localStorage` persistence (unlike `stylesData`), so persistence is proven
 * via `commons/inline-edit/emptyDropdown.helpers.ts`'s `fetchMockItemBySearch` — an in-page
 * `fetch('/api/items?search=...')` that reads this same mutable array back through the real
 * MirageJS handler — never `page.reload()` (which re-executes the bundle and reseeds the
 * module-level array back to these builders).
 *
 * Isolable via `?search=ClearOptionProbe` (unique substring, absent from every generated/other
 * fixture name), with a sub-phrase per scenario for further narrowing.
 */

const baseClearOptionRow = (
  id: number,
  name: string,
  userDefinedId: string,
  overrides: Record<string, unknown> = {}
) => ({
  id,
  name,
  userDefinedId,
  description: null,
  brand: brandOptions[0],
  organization: organizations[0],
  supplier: suppliers[0],
  companyContactPerson: contactPersons[0],
  supplierContactPerson: null,
  adminUnit: null,
  logo: null,
  isActive: true,
  suppliedBy: null,
  readyForExport: false,
  updatedAfterLastExport: false,
  lastUpdated: '2026-01-12T09:00:00',
  deadline: null,
  etd: null,
  erpItemId: null,
  defaultQty: 3,
  nameGeneratorExtraText: null,
  physicalReferenceNumber: null,
  categories: [],
  customFields: [],
  ...overrides,
});

const CARE_INSTRUCTION_CF_ID = 10; // "Care instruction" — single-select AllowedValue, NOT mandatory
export const FABRIC_COMPOSITION_CF_ID = 16; // "Fabric composition" — single-select, mandatory FOR EXPORT

const careInstructionCfEntry = (cfEntryId: number, value: string, allowedValueId: number) => ({
  id: cfEntryId,
  customFieldId: CARE_INSTRUCTION_CF_ID,
  name: 'Care instruction',
  userDefinedId: 'CARE',
  type: 0,
  value,
  itemCustomFieldAllowedValueId: allowedValueId,
  customFieldAllowedValueUserDefinedID: null,
  isMandatory: false,
  isSupplierEditable: true,
  internalOnly: false,
  maxChar: null,
  isMultiLine: false,
  allowedDecimals: 0,
  isMultipleAllowedValue: false,
  parentCustomFieldId: null,
  parentCustomFieldName: null,
});

const fabricCompositionCfEntry = (cfEntryId: number, value: string, allowedValueId: number) => ({
  id: cfEntryId,
  customFieldId: FABRIC_COMPOSITION_CF_ID,
  name: 'Fabric composition',
  userDefinedId: 'FABRICCOMP',
  type: 0,
  value,
  itemCustomFieldAllowedValueId: allowedValueId,
  customFieldAllowedValueUserDefinedID: null,
  isMandatory: true,
  isSupplierEditable: true,
  internalOnly: false,
  maxChar: null,
  isMultiLine: false,
  allowedDecimals: 0,
  isMultipleAllowedValue: false,
  parentCustomFieldId: null,
  parentCustomFieldName: null,
});

/** dl-63890/dl-63891 (Testmo 2273367/2273368) — Unit populated, optional. */
export const clearOptionUnitPopulated = baseClearOptionRow(
  96501,
  'ClearOptionProbe Unit Populated',
  'IT-96501',
  { adminUnit: adminUnits[0] }
);

/** dl-63894 (Testmo 2273371) — Unit already empty; Clear must not be offered. */
export const clearOptionUnitEmpty = baseClearOptionRow(
  96502,
  'ClearOptionProbe Unit Empty',
  'IT-96502',
  { adminUnit: null }
);

/**
 * dl-63888/dl-63889/dl-63902 (Testmo 2273365/2273366/2273379) — Supplier ASSIGNED (so the
 * contact field is editable, not the "Select a supplier first" placeholder) with a populated
 * Supplier contact. Also doubles as the "Supplier is read-only once assigned" evidence for
 * dl-63897 (2273374), and the isolation row for dl-63902 (clearing the contact must not touch
 * the still-assigned Supplier).
 */
export const clearOptionSupplierContactPopulated = baseClearOptionRow(
  96503,
  'ClearOptionProbe SupplierContact Populated',
  'IT-96503',
  {
    supplier: suppliers[0],
    supplierContactPerson: { id: contactPersons[0].id, name: contactPersons[0].name },
  }
);

/** dl-63892/dl-63893 (Testmo 2273369/2273370) — a plain (non-mandatory) single-select CF populated. */
export const clearOptionCfSinglePopulated = baseClearOptionRow(
  96504,
  'ClearOptionProbe CfSingle Populated',
  'IT-96504',
  { customFields: [careInstructionCfEntry(965049, 'Machine wash', 101)] }
);

/**
 * dl-63899 (Testmo 2273376) — a custom field marked mandatory FOR EXPORT, on a row that IS ready
 * for export (`readyForExport: true`), so the Clear-suppression rule
 * (`isMandatory && row.readyForExport`) is genuinely exercised — not merely "isMandatory: true"
 * in the abstract (see the epic's Supplier-divergence note: this is a second, distinct notion of
 * "mandatory" from the plain-mandatory fields above).
 */
export const clearOptionCfMandatoryExportReady = baseClearOptionRow(
  96505,
  'ClearOptionProbe CfMandatoryExport Ready',
  'IT-96505',
  {
    readyForExport: true,
    // Both a mandatory-for-export CF (Fabric composition) AND a plain, non-mandatory CF (Care
    // instruction) populated on the SAME row, so a single spec can flip the `isMandatory` flag
    // (via an `apiTransform` override of `GET /api/meta/items/customfields`) and prove the two
    // fields' Clear behaviour swaps — the difference is attributable to the flag alone, not to
    // which column it happens to be (dl-63899's explicit "flip the flag" acceptance criterion).
    customFields: [
      fabricCompositionCfEntry(965050, 'Cotton', 1601),
      careInstructionCfEntry(965051, 'Machine wash', 101),
    ],
  }
);

/**
 * dl-63900 (Testmo 2273377) — typing-filter probe. A dedicated row (rather than reusing
 * `clearOptionUnitPopulated`) so this spec's `apiTransform`-free, real-refetch interactions never
 * collide with a value another test in the same file just mutated.
 */
export const clearOptionTypingFilterProbe = baseClearOptionRow(
  96506,
  'ClearOptionProbe TypingFilter',
  'IT-96506',
  { adminUnit: adminUnits[0] }
);

/** dl-63901 (Testmo 2273378) — dedicated "saves immediately, no toast" probe (Supplier contact). */
export const clearOptionSaveUxProbe = baseClearOptionRow(
  96507,
  'ClearOptionProbe SaveUx',
  'IT-96507',
  {
    supplier: suppliers[0],
    supplierContactPerson: { id: contactPersons[1].id, name: contactPersons[1].name },
  }
);

/** All new rows for this epic — spread into `itemsData` in `items.ts`. */
export const clearOptionDropdownItemRows = [
  clearOptionUnitPopulated,
  clearOptionUnitEmpty,
  clearOptionSupplierContactPopulated,
  clearOptionCfSinglePopulated,
  clearOptionCfMandatoryExportReady,
  clearOptionTypingFilterProbe,
  clearOptionSaveUxProbe,
];
